import { z } from "zod";
/**
 * The 12 Entity Roles a Stable membership can hold. Travelling Lad and
 * Travelling Head Lad are permission-equivalent to Groom and Head Lad
 * respectively (COMPANY.md "Roles") — the distinction is a display label
 * only, never a separate permission tier.
 */
export const ENTITY_ROLES = [
    "TRAINER",
    "CO_TRAINER",
    "ASSISTANT_TRAINER",
    "RACING_MANAGER",
    "HEAD_LAD",
    "GROOM",
    "FEEDMAN",
    "VET",
    "FARRIER",
    "RIDER",
    "TRAVELLING_LAD",
    "TRAVELLING_HEAD_LAD",
];
export const EntityRoleSchema = z.enum(ENTITY_ROLES);
/** Stable features gated by the Entity Role permission matrix. */
export const STABLE_FEATURES = [
    "HORSE_DETAILS",
    "STAFF_RECORDS",
    "CARE_RECORDS",
    "TRAINING_SCHEDULER_PLANNING",
    "TRAINING_SCHEDULER_LOGGING",
    "VET_RECORDS",
    "FARRIER_RECORDS",
    "VACCINATIONS",
    "ACCOUNTING",
    "HIRE",
    "LINK_AN_OWNER",
    "STABLE_TRANSFER",
];
export const StableFeatureSchema = z.enum(STABLE_FEATURES);
const FEATURE_ACCESS_LEVELS = ["FULL", "VIEW", "NONE"];
export const FeatureAccessLevelSchema = z.enum(FEATURE_ACCESS_LEVELS);
const ACCESS_RANK = {
    NONE: 0,
    VIEW: 1,
    FULL: 2,
};
export function accessLevelAtLeast(level, required) {
    return ACCESS_RANK[level] >= ACCESS_RANK[required];
}
/**
 * Roles that are permission-equivalent for matrix lookup purposes, per
 * `docs/products/stable.md` "Membership and Entity Role permissions".
 * Travelling Lad/Head Lad mirror Groom/Head Lad; Co-Trainer mirrors Trainer.
 */
const MATRIX_ROLE = {
    TRAINER: "TRAINER",
    CO_TRAINER: "TRAINER",
    ASSISTANT_TRAINER: "ASSISTANT_TRAINER",
    RACING_MANAGER: "RACING_MANAGER",
    HEAD_LAD: "HEAD_LAD",
    GROOM: "GROOM",
    FEEDMAN: "FEEDMAN",
    VET: "VET",
    FARRIER: "FARRIER",
    RIDER: "RIDER",
    TRAVELLING_LAD: "GROOM",
    TRAVELLING_HEAD_LAD: "HEAD_LAD",
};
function row(full, view) {
    const result = {};
    for (const role of ENTITY_ROLES) {
        result[role] = full.includes(MATRIX_ROLE[role])
            ? "FULL"
            : view.includes(MATRIX_ROLE[role])
                ? "VIEW"
                : "NONE";
    }
    return result;
}
const TRAINER_TIER = [
    "TRAINER",
    "ASSISTANT_TRAINER",
    "RACING_MANAGER",
];
const MANAGEMENT_TIER = ["TRAINER", "ASSISTANT_TRAINER"];
const HANDS_ON_TIER = [
    "TRAINER",
    "ASSISTANT_TRAINER",
    "RACING_MANAGER",
    "HEAD_LAD",
    "GROOM",
    "FEEDMAN",
    "RIDER",
];
/**
 * Default per-feature, per-role access level, exactly matching the
 * "Membership and Entity Role permissions" table in
 * `docs/products/stable.md`. This is the load-bearing configuration every
 * later Sealed-feature check reads through {@link defaultAccessLevel} and
 * {@link structuralCeiling} — do not duplicate this matrix elsewhere.
 */
const DEFAULT_PERMISSION_MATRIX = {
    HORSE_DETAILS: row(TRAINER_TIER, [
        "HEAD_LAD",
        "GROOM",
        "FEEDMAN",
        "VET",
        "FARRIER",
        "RIDER",
    ]),
    STAFF_RECORDS: row(MANAGEMENT_TIER, []),
    CARE_RECORDS: row(HANDS_ON_TIER, ["VET", "FARRIER"]),
    TRAINING_SCHEDULER_PLANNING: row(TRAINER_TIER, []),
    TRAINING_SCHEDULER_LOGGING: row([...TRAINER_TIER, "RIDER"], []),
    VET_RECORDS: row([...TRAINER_TIER, "VET"], ["HEAD_LAD", "GROOM", "FEEDMAN", "RIDER", "FARRIER"]),
    FARRIER_RECORDS: row([...TRAINER_TIER, "FARRIER"], ["HEAD_LAD", "GROOM", "FEEDMAN", "RIDER", "VET"]),
    VACCINATIONS: row([...TRAINER_TIER, "VET"], ["HEAD_LAD", "GROOM", "FEEDMAN", "RIDER", "FARRIER"]),
    ACCOUNTING: row(MANAGEMENT_TIER, []),
    HIRE: row(MANAGEMENT_TIER, []),
    LINK_AN_OWNER: row(MANAGEMENT_TIER, []),
    STABLE_TRANSFER: row(MANAGEMENT_TIER, []),
};
/**
 * Features with a closed eligible-role set: every role outside that set is
 * structurally excluded and can never be raised by a per-member override
 * (`docs/products/stable.md`: "no override can grant Accounting access to a
 * role structurally excluded from it ... not by default-with-override").
 * Features not listed here grant at least VIEW to every role by default, so
 * any role may be raised up to FULL by override.
 */
const CLOSED_ELIGIBILITY_FEATURES = new Set([
    "STAFF_RECORDS",
    "TRAINING_SCHEDULER_PLANNING",
    "TRAINING_SCHEDULER_LOGGING",
    "ACCOUNTING",
    "HIRE",
    "LINK_AN_OWNER",
    "STABLE_TRANSFER",
]);
export function defaultAccessLevel(role, feature) {
    return DEFAULT_PERMISSION_MATRIX[feature][role];
}
/**
 * The maximum access level a role could ever hold on a feature, regardless
 * of a Trainer/Co-Trainer override. Trainer and Co-Trainer are always FULL
 * everywhere ("permission-equivalent ... full, unrestricted Stable access")
 * and are never overridable down or up. For a closed-eligibility feature, a
 * role outside its default Full-access set is capped at its current default
 * (typically NONE); for every other feature, every role's ceiling is FULL.
 */
export function structuralCeiling(role, feature) {
    if (role === "TRAINER" || role === "CO_TRAINER")
        return "FULL";
    if (CLOSED_ELIGIBILITY_FEATURES.has(feature)) {
        return defaultAccessLevel(role, feature);
    }
    return "FULL";
}
/** Trainer and Co-Trainer access can never be overridden per member. */
export function isOverridableRole(role) {
    return role !== "TRAINER" && role !== "CO_TRAINER";
}
